import { get, post } from 'api/client';
import PageTypeUtils from 'app/utils/pageType';
import Config from 'config';
import {
  SocialLoginBusiness,
  SocialLoginPage,
  SocialLoginTokenType,
} from 'types/SocialLogin';
import { TYPE } from '@kontentino/kontentino-constants/Pages';

export function getSocialNetworkForRequest(pageType: number) {
  let socialNetwork = PageTypeUtils.getName(pageType);

  if (socialNetwork === PageTypeUtils.name[TYPE.GOOGLE_MY_BUSINESS]) {
    socialNetwork = 'google';
  }

  return socialNetwork;
}

export function getPageTypeFromSocialNetworkRequest(
  socialNetwork: string,
): number {
  let pageType = PageTypeUtils.getNumberFromName(socialNetwork);

  if (socialNetwork === 'google') {
    pageType = TYPE.GOOGLE_MY_BUSINESS;
  }

  return pageType;
}

const SocialLoginApi = {
  requestLoginUrl: async (
    pageType: number,
    params: {
      redirectUrl?: string;
      scopes?: string[];
      tokenType?: SocialLoginTokenType;
    },
  ): Promise<{
    loginUrl: string;
    redirectUrl: string | null;
    userKey?: string;
  }> => {
    const socialNetwork = getSocialNetworkForRequest(pageType);

    return get(`/api/${socialNetwork}/auth/redirect`, {
      baseURL: Config.SOCIAL_LOGIN_SERVICE_ENDPOINT,
      params,
    });
  },
  submitSystemUserToken: async (
    pageType: number,
    data: { accessToken: string },
  ): Promise<{ userKey: string; clientBusinessId: string }> => {
    const socialNetwork = getSocialNetworkForRequest(pageType);

    return post(
      `/api/${socialNetwork}/system-user/token`,
      { accessToken: data.accessToken },
      { baseURL: Config.SOCIAL_LOGIN_SERVICE_ENDPOINT },
    );
  },
  addPagesToSocialLogin: async (
    pageType: number,
    data: {
      pages: SocialLoginPage[];
    },
  ): Promise<SocialLoginPage[]> => {
    const socialNetwork = getSocialNetworkForRequest(pageType);

    return post(
      `/api/${socialNetwork}/pages`,
      {
        pages: data.pages,
      },
      {
        baseURL: Config.SOCIAL_LOGIN_SERVICE_ENDPOINT,
      },
    );
  },
  addProfiles: async (
    pageType: number,
    data: {
      timezone: string;
      pages: SocialLoginPage[];
      clientBusinessId?: string | null;
      tokenType?: SocialLoginTokenType;
      userKey?: string | null;
    },
  ): Promise<number[]> => {
    const socialNetwork = getSocialNetworkForRequest(pageType);

    const socialLoginResponse: SocialLoginPage[] = await post(
      `/api/${socialNetwork}/pages`,
      {
        pages: data.pages,
        ...(data.clientBusinessId
          ? { clientBusinessId: data.clientBusinessId }
          : {}),
        ...(data.tokenType ? { tokenType: data.tokenType } : {}),
        ...(data.userKey ? { userKey: data.userKey } : {}),
      },
      {
        baseURL: Config.SOCIAL_LOGIN_SERVICE_ENDPOINT,
      },
    );

    return post(
      '/pages',
      {
        timezone: data.timezone,
        data: socialLoginResponse,
      },
      {
        baseURL: Config.API_SERVICE_ENDPOINT,
      },
    );
  },
  fetchPages: async (
    userKey?: string,
  ): Promise<{
    status: 'waiting' | 'done';
    pages: SocialLoginPage[];
    clientBusinessId?: string | null;
    partial?: boolean;
    businesses?: SocialLoginBusiness[];
  }> => {
    if (!userKey) {
      throw new Error('User key is required');
    }

    return get('/api/fetch/pages', {
      params: { userKey },
      baseURL: Config.SOCIAL_LOGIN_SERVICE_ENDPOINT,
    });
  },
  connectedPagesExternalIds: async (pageType: number): Promise<string[]> => {
    return get(
      `/settings/authorization/connected-external-page-ids?socialNetwork=${getSocialNetworkForRequest(
        pageType,
      )}`,
      {
        baseURL: Config.API_SERVICE_ENDPOINT,
      },
    );
  },
  toggleShareAccessToken: ({
    isShared,
    pageType,
  }: {
    isShared: boolean;
    pageType: number;
  }) => {
    const socialNetwork = getSocialNetworkForRequest(pageType);

    return post(
      `/settings/authorization/${socialNetwork}/share-access-token`,
      {
        isShared,
      },
      {
        baseURL: Config.API_SERVICE_ENDPOINT,
      },
    );
  },
};

export default SocialLoginApi;
